import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Button,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from 'react-query';
import { leadsAPI } from '../../services/api';
import { analyticsAPI } from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import HealthBadge from '../../components/Management/HealthBadge';
import { LabelWithInfo } from '../../components/InfoTooltip';
import { managementHelp } from '../../content/managementHelp';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'last_5_days', label: 'Last 5 days' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
];

const VIEW_OPTIONS = [
  { value: 'country', label: 'Country wise' },
  { value: 'sales_person', label: 'Sales person wise' },
];

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#0288d1', '#c62828', '#558b2f', '#6a1b9a', '#00838f', '#ef6c00'];

function useChartData(byDate, dates, groups) {
  return useMemo(() => {
    if (!dates || !byDate) return [];
    return dates.map((date) => {
      const row = { date: date.slice(5) };
      groups.forEach((g) => {
        row[g] = byDate[date] && byDate[date][g] != null ? byDate[date][g] : 0;
      });
      return row;
    });
  }, [byDate, dates, groups]);
}

function DateWiseBarChart({ data, groups }) {
  if (!data || data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No data for this period
      </Typography>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Legend />
        {groups.map((group, idx) => (
          <Bar key={group} dataKey={group} stackId="a" fill={COLORS[idx % COLORS.length]} name={group} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function CurrentStatusChart({ currentStatus, scoped }) {
  const data = useMemo(() => {
    if (!currentStatus || currentStatus.length === 0) return [];
    return currentStatus.map((row) => ({
      name: scoped ? 'My pipeline' : (row.sales_person_name || 'Unassigned'),
      New: row.new_count || 0,
      'WIP before demo': row.wip_before_demo_count || 0,
      'WIP after demo': row.wip_after_demo_count || 0,
    }));
  }, [currentStatus, scoped]);

  if (data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No active leads
      </Typography>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: scoped ? 0 : 60 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="name"
          angle={scoped ? 0 : -35}
          textAnchor={scoped ? 'middle' : 'end'}
          height={scoped ? 30 : 80}
          tick={{ fontSize: 11 }}
        />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Legend />
        <Bar dataKey="New" fill="#1976d2" name="New lead" />
        <Bar dataKey="WIP before demo" fill="#ed6c02" name="WIP before demo" />
        <Bar dataKey="WIP after demo" fill="#2e7d32" name="WIP after demo" />
      </BarChart>
    </ResponsiveContainer>
  );
}

function FollowUpAgeingSection({ stuckLeadsPath }) {
  const navigate = useNavigate();
  const { data: res, isLoading, error } = useQuery(
    ['followUpAgeing'],
    () => analyticsAPI.getFollowUpAgeing().then((r) => r.data?.data),
    { staleTime: 60000 }
  );
  const ageing = res?.followUpAgeing;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error.response?.data?.message || error.message || 'Failed to load follow-up ageing'}
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 2, mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <LabelWithInfo
          label={
            <Typography variant="h6" component="h2">
              Follow-up ageing (stuck after demo)
            </Typography>
          }
          title={managementHelp.ageing.section.title}
          body={managementHelp.ageing.section.body}
        />
        {ageing && <HealthBadge healthStatus={ageing.healthStatus} healthLabel={ageing.healthLabel} />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Leads with a completed demo still in the pipeline — current assigned_to only, as of today.
      </Typography>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        {ageing?.stuckTotal ?? 0} leads waiting
      </Typography>
      <Grid container spacing={1}>
        {(ageing?.buckets || []).map((b) => (
          <Grid item xs={6} sm={3} key={b.key}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => navigate(`${stuckLeadsPath}?bucket=${b.key}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`${stuckLeadsPath}?bucket=${b.key}`)}
              sx={{
                p: 1.5,
                border: 2,
                borderColor:
                  b.healthStatus === 'poor'
                    ? 'error.main'
                    : b.healthStatus === 'needs_attention'
                      ? 'warning.main'
                      : 'success.light',
                borderRadius: 1,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography variant="caption" display="block">
                {b.label}
              </Typography>
              <Typography variant="h6">{b.count}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Button sx={{ mt: 2 }} size="small" onClick={() => navigate(stuckLeadsPath)}>
        View stuck leads list
      </Button>
    </Paper>
  );
}

const LeadsAnalytics = () => {
  const { user } = useAuth();
  const isMyLeadsOnly = user?.role === 'sales';
  const stuckLeadsPath = '/leads/stuck-leads';

  const [period, setPeriod] = useState('last_7_days');
  const [view, setView] = useState('country');

  const { data, isLoading, error } = useQuery(
    ['leads-analytics', period, view],
    () => leadsAPI.getAnalytics({ period, view }).then((res) => res.data)
  );

  const leadCreatedData = useChartData(
    data?.leadCreated?.byDate,
    data?.leadCreated?.dates,
    data?.leadCreated?.groups || []
  );
  const leadWonData = useChartData(
    data?.leadWon?.byDate,
    data?.leadWon?.dates,
    data?.leadWon?.groups || []
  );
  const leadLostData = useChartData(
    data?.leadLost?.byDate,
    data?.leadLost?.dates,
    data?.leadLost?.groups || []
  );

  const viewOptions = isMyLeadsOnly ? VIEW_OPTIONS.filter((o) => o.value === 'country') : VIEW_OPTIONS;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        {isMyLeadsOnly ? 'My Leads Analytics' : 'Leads Analytics'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isMyLeadsOnly
          ? 'Metrics for leads currently assigned to you. Date filters use acquired date.'
          : 'All metrics use acquired date for date filters. View by country or by sales person.'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Period (by Acquired date)</InputLabel>
          <Select
            value={period}
            label="Period (by Acquired date)"
            onChange={(e) => setPeriod(e.target.value)}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {viewOptions.length > 1 && (
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, v) => v != null && setView(v)}
            size="small"
          >
            {viewOptions.map((opt) => (
              <ToggleButton key={opt.value} value={opt.value}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        {data?.period && (
          <Typography variant="body2" color="text.secondary">
            {data.period.from} to {data.period.to}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.response?.data?.message || error.message || 'Failed to load analytics'}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Lead created (by acquired date)
                </Typography>
                <DateWiseBarChart data={leadCreatedData} groups={data?.leadCreated?.groups || []} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Lead won
                </Typography>
                <DateWiseBarChart data={leadWonData} groups={data?.leadWon?.groups || []} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Lead lost
                </Typography>
                <DateWiseBarChart data={leadLostData} groups={data?.leadLost?.groups || []} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {isMyLeadsOnly ? 'My current pipeline' : 'Current status (sales person wise)'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  New lead, WIP before demo, WIP after demo — active leads only (excluding won/lost/dropped)
                </Typography>
                <CurrentStatusChart currentStatus={data?.currentStatus} scoped={isMyLeadsOnly} />
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <FollowUpAgeingSection stuckLeadsPath={stuckLeadsPath} />
          </Box>
        </>
      )}
    </Container>
  );
};

export default LeadsAnalytics;
